import { sumRounded } from '../../../Utils/decimalUtils';
import { VossPricingScheme } from '../../../Constants/VOSS/vossPricingScheme';
import { VossProductType } from '../../../Constants/VOSS/vossProductType';
import { VossUseRevenueRecognition } from '../../../Constants/VOSS/vossUseRevenueRecognition';
import { isValidLength } from './planUtils';
import { isOptionalWholeNumber, sortProductGroups, trimmedOrUndefined } from './tierUtils';
import type {
    ChargePlanDraft,
    CreatePackageChargePlanRequest,
    GroupPricingDraft,
    PackageChargePlan,
    PackagePlan,
    PackageProductGroup,
    PackageTier,
    PricingStepDraft,
    ProductGroupPricing,
    ProductGroupPricingRequest,
    ProductPricing,
    ProductPricingDraft,
    UpdatePackageChargePlanRequest,
} from './Packages.types';

export interface PricedGroup {
    group: PackageProductGroup;
    pricing: ProductGroupPricing;
    isRemovedFromTier: boolean;
}

export const findChargePlan = (chargePlans: PackageChargePlan[], tierId: string | undefined, planId: string | undefined): PackageChargePlan | undefined =>
    chargePlans.find((chargePlan) => chargePlan.tierId === tierId && chargePlan.planId === planId);

export const chargePlanTitle = (tier: PackageTier | undefined, plan: PackagePlan | undefined): string => `${tier?.name ?? ''} × ${plan?.name ?? ''}`;

export const chargePlanTotal = (chargePlan: PackageChargePlan): number => sumRounded((chargePlan.productGroupPricings ?? []).map((pricing) => pricing.price ?? 0));

export const chargePlanProductCount = (chargePlan: PackageChargePlan): number =>
    (chargePlan.productGroupPricings ?? []).reduce((sum, pricing) => sum + (pricing.productPricings ?? []).length, 0);

// pricings only carry ids; the names, types and order come from the tier's product groups. A pricing can outlive its group in the
// tier and still counts towards the totals, so it is listed last rather than hidden
export const pricedGroups = (chargePlan: PackageChargePlan, tier: PackageTier | undefined): PricedGroup[] => {
    const allPricings = chargePlan.productGroupPricings ?? [];
    const pricings = new Map(allPricings.map((pricing) => [pricing.productGroupId, pricing]));
    const tierGroupIds = new Set((tier?.productGroups ?? []).map((group) => group.id));

    const inTier = sortProductGroups(tier?.productGroups ?? []).flatMap((group) => {
        const pricing = pricings.get(group.id);
        return pricing === undefined ? [] : [{ group, pricing, isRemovedFromTier: false }];
    });
    const removed =
        tier === undefined
            ? []
            : allPricings
                  .filter((pricing) => !tierGroupIds.has(pricing.productGroupId))
                  .map((pricing) => ({ group: { id: pricing.productGroupId }, pricing, isRemovedFromTier: true }));

    return [...inTier, ...removed];
};

// VOSS only takes tier pricing on License and Usage products; Main and Addon need a unit price
export const allowsTierPricing = (productType: number | undefined): boolean => productType === VossProductType.License || productType === VossProductType.Usage;

export const emptyPricingStep = (): PricingStepDraft => ({ upperLimit: null, unitPrice: null, flatPrice: 0, repeatEvery: null });

// the new step goes before the last one, which has to stay unbounded
export const withAddedStep = (steps: PricingStepDraft[]): PricingStepDraft[] => [...steps.slice(0, -1), emptyPricingStep(), ...steps.slice(-1)];

const toProductPricingDraft = (productId: string, pricing: ProductPricing | undefined, productType: number | undefined): ProductPricingDraft => {
    // usage is billed by consumption, so an unpriced usage product starts on tier steps
    const isTiered = pricing === undefined ? productType === VossProductType.Usage : pricing.pricingScheme === VossPricingScheme.TierPricing;
    const steps = (pricing?.tierPricing ?? []).map((step) => ({
        upperLimit: step.upperLimit ?? null,
        unitPrice: step.unitPrice ?? 0,
        flatPrice: step.flatPrice ?? 0,
        repeatEvery: step.repeatEvery ?? null,
    }));

    return {
        productId,
        pricingScheme: isTiered ? VossPricingScheme.TierPricing : VossPricingScheme.UnitPricing,
        unitPrice: pricing?.unitPrice ?? null,
        includedQuantity: pricing?.includedQuantity ?? null,
        maxQuantity: pricing?.maxQuantity ?? null,
        articleNumber: pricing?.articleNumber ?? '',
        tierPricingGroup: pricing?.tierPricingGroup ?? '',
        rank: pricing?.rank ?? null,
        steps: steps.length > 0 ? steps : [emptyPricingStep()],
    };
};

const toGroupPricingDraft = (productGroupId: string, pricing: ProductGroupPricing | undefined, products: { id?: string; type?: number }[]): GroupPricingDraft => {
    const productPricings = new Map((pricing?.productPricings ?? []).map((productPricing) => [productPricing.productId, productPricing]));

    return {
        productGroupId,
        articleNumber: pricing?.articleNumber ?? '',
        invoiceGroup: pricing?.invoiceGroup ?? '',
        useRevenueRecognition: pricing?.useRevenueRecognition ?? VossUseRevenueRecognition.Inherit,
        products: products.map((product) => toProductPricingDraft(product.id ?? '', productPricings.get(product.id), product.type)),
    };
};

// every group and product of the tier is listed, so a new charge plan starts complete and an edited one picks up products added since
export const toChargePlanDraft = (tier: PackageTier | undefined, planId: string, chargePlan?: PackageChargePlan): ChargePlanDraft => {
    const pricings = new Map((chargePlan?.productGroupPricings ?? []).map((pricing) => [pricing.productGroupId, pricing]));

    return {
        tierId: tier?.id ?? '',
        planId,
        invoicingId: chargePlan?.invoicingId ?? '',
        groups: sortProductGroups(tier?.productGroups ?? []).map((group) => toGroupPricingDraft(group.id ?? '', pricings.get(group.id), group.products ?? [])),
    };
};

const toProductGroupPricingRequest = (group: GroupPricingDraft): ProductGroupPricingRequest => ({
    productGroupId: group.productGroupId,
    articleNumber: trimmedOrUndefined(group.articleNumber),
    invoiceGroup: trimmedOrUndefined(group.invoiceGroup),
    useRevenueRecognition: group.useRevenueRecognition,
    productPricings: group.products.map((product) => ({
        productId: product.productId,
        includedQuantity: product.includedQuantity,
        maxQuantity: product.maxQuantity,
        articleNumber: trimmedOrUndefined(product.articleNumber),
        tierPricingGroup: trimmedOrUndefined(product.tierPricingGroup),
        rank: product.rank,
        // VOSS takes either a unit price or tier steps, never both
        ...(product.pricingScheme === VossPricingScheme.TierPricing
            ? {
                  tierPricing: product.steps.map((step) => ({
                      upperLimit: step.upperLimit,
                      unitPrice: step.unitPrice ?? 0,
                      flatPrice: step.flatPrice ?? 0,
                      repeatEvery: step.repeatEvery,
                  })),
              }
            : { unitPrice: product.unitPrice }),
    })),
});

export const toCreateChargePlanRequest = (draft: ChargePlanDraft): CreatePackageChargePlanRequest => ({
    tierId: draft.tierId,
    planId: draft.planId,
    invoicingId: trimmedOrUndefined(draft.invoicingId),
    productGroupPricings: draft.groups.map(toProductGroupPricingRequest),
});

export const toUpdateChargePlanRequest = (draft: ChargePlanDraft): UpdatePackageChargePlanRequest => ({
    invoicingId: trimmedOrUndefined(draft.invoicingId),
    productGroupPricings: draft.groups.map(toProductGroupPricingRequest),
});

// the PUT carries every group pricing, so the untouched groups go back as they were read, minus groups and products since removed from the tier:
// those pricings can't bill anything any more, and VOSS rejects them for products
export const withRevenueRecognition = (
    chargePlan: PackageChargePlan,
    tier: PackageTier | undefined,
    productGroupId: string,
    mode: VossUseRevenueRecognition
): UpdatePackageChargePlanRequest => {
    const tierProducts = new Map((tier?.productGroups ?? []).map((group) => [group.id, (group.products ?? []).map((product) => product.id)]));

    return toUpdateChargePlanRequest({
        tierId: chargePlan.tierId ?? '',
        planId: chargePlan.planId ?? '',
        invoicingId: chargePlan.invoicingId ?? '',
        groups: (chargePlan.productGroupPricings ?? [])
            .filter((pricing) => tier === undefined || tierProducts.has(pricing.productGroupId))
            .map((pricing) => {
                const products = tierProducts.get(pricing.productGroupId);
                const kept = (pricing.productPricings ?? []).filter((productPricing) => products?.includes(productPricing.productId) ?? true);
                const group = toGroupPricingDraft(
                    pricing.productGroupId ?? '',
                    pricing,
                    kept.map((productPricing) => ({ id: productPricing.productId }))
                );
                return pricing.productGroupId === productGroupId ? { ...group, useRevenueRecognition: mode } : group;
            }),
    });
};

export const isPrice = (value: number | null): boolean => value !== null && Number.isFinite(value) && value >= 0;

// an empty flat price is sent as 0, so only a negative one is wrong
export const isOptionalPrice = (value: number | null): boolean => value === null || isPrice(value);

export const isValidRepeatEvery = (value: number | null): boolean => value === null || (isOptionalWholeNumber(value) && value > 0);

// every step but the last needs an upper limit above the previous one; the last stays unbounded
export const isValidStepLimit = (steps: PricingStepDraft[], index: number): boolean => {
    const { upperLimit } = steps[index];
    if (index === steps.length - 1) return upperLimit === null;

    const previous = index === 0 ? null : steps[index - 1].upperLimit;
    return upperLimit !== null && isValidLength(upperLimit) && (previous === null || upperLimit > previous);
};

export const isValidProductPricing = (product: ProductPricingDraft): boolean =>
    isOptionalWholeNumber(product.includedQuantity) &&
    isOptionalWholeNumber(product.maxQuantity) &&
    isOptionalWholeNumber(product.rank) &&
    (product.pricingScheme === VossPricingScheme.TierPricing
        ? product.steps.length > 0 &&
          product.steps.every(
              (step, index) => isValidStepLimit(product.steps, index) && isPrice(step.unitPrice) && isOptionalPrice(step.flatPrice) && isValidRepeatEvery(step.repeatEvery)
          )
        : isPrice(product.unitPrice));

// VOSS rejects a group pricing without an article number, although its swagger marks the field optional
export const isValidChargePlanDraft = (draft: ChargePlanDraft): boolean =>
    draft.tierId !== '' &&
    draft.planId !== '' &&
    draft.groups.length > 0 &&
    draft.groups.every((group) => group.articleNumber.trim().length > 0 && group.products.every(isValidProductPricing));
